// @flow
import React, { type Node, Fragment, PureComponent } from 'react';
import i18n from 'app/common/lib/i18n';
import Impressions from 'app/packages/impression-logging';
import LoadingMask from 'app/common/react/components/growth/unauth/signup/LoadingMask/LoadingMask';
import NuxStepTracker from 'app/common/react/components/growth/activation/NuxModal/NuxStepTracker';
import NuxTableOfContents from 'app/common/react/components/growth/activation/NuxModal/NuxTableOfContents';
import type { RequestContextType } from 'app/common/react/components/RequestContext';
import { Experiment } from 'app/common/react/components/experiments';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import {
  MIN_HF_STEP_WIDTH,
  type NuxStep,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Divider, IconButton, Layer } from 'gestalt';

type Props = {|
  children: Node,
  displayHide: boolean,
  onHide?: () => void,
  onPrevStepClick: () => void,
  nuxStep: NuxStep,
  steps: Array<NuxStep>,
  isLoading?: boolean,
  isRenux: ?boolean,
  isSimpleRenux: ?boolean,
  requestContext: RequestContextType,
  showCounter: boolean,
  showTableContents?: boolean,
|};

type State = {|
  animateIn: boolean,
|};

const HEIGHT = 640;
const HEIGHT_WITH_BIZ_BUSINESS_TYPE_EXPERIMENT = 692; // used by enabled group in experiment biz_growth_update_business_type
const SMALL_WIDTH = 544;
const LARGE_WIDTH = 800;

const SMALL_STEPS = [
  'NuxDismissableWelcome',
  'NuxWelcomeNameStep',
  'NuxAboutYouStep',
  'NuxGenderStep',
  'NuxLocaleCountryStep',
  'NuxLocaleStep',
  'NuxCountryStep',
];

const EXTRA_VERTICAL_HEIGHT = 110; // header + top/bottom margin of step
const SHORT_HEIGHT = 280;
const TALL_HEIGHT_HF = 575;

class NuxModal extends PureComponent<Props, State> {
  state: State = {
    animateIn: true,
  };

  windowScrollInterval: IntervalID;
  windowScrollTimer: TimeoutID;

  componentDidMount() {
    const { isRenux, isSimpleRenux, requestContext } = this.props;

    if (document.body) {
      document.body.style.overflow = 'hidden'; // prevent scrolling
    }

    const reNUXMovingBgExp = new SingleTreatmentExp(requestContext, 'web_renux_moving_bg_pins');
    if (!__SERVER__ && isRenux && !isSimpleRenux && reNUXMovingBgExp.enabled()) {
      this.windowScrollInterval = window.setInterval(() => {
        window.scrollBy(0, 1);
      }, 50); // auto-scroll down 1px/50ms
      this.windowScrollTimer = window.setTimeout(() => {
        window.clearInterval(this.windowScrollInterval);
      }, 120000); // scroll for 2 minutes at max
    }

    // pausing impressions for pseduo-modal obstruction
    // see webapp/app/packages/impression-logging/Impressions.js for more info
    Impressions.pause();
    this.runAnimation();
  }

  componentDidUpdate() {
    this.runAnimation();
  }

  componentWillUnmount() {
    if (!__SERVER__ && this.windowScrollInterval) {
      window.clearInterval(this.windowScrollInterval); //stop auto-scrolling
    }
    if (!__SERVER__ && this.windowScrollTimer) {
      window.clearTimeout(this.windowScrollTimer); //remove auto-scrolling timeout
    }
    if (document.body) {
      document.body.style.overflow = ''; //renable scrolling
    }

    Impressions.resume();
  }

  runAnimation = () => {
    window.setTimeout(() => this.setState({ animateIn: false }), 200);
  };

  renderOnHomefeed = () => {
    const { children, nuxStep, steps, isLoading, onPrevStepClick } = this.props;
    const { animateIn } = this.state;

    const height =
      ['NuxPicker', 'NuxEducationLoader'].includes(nuxStep.moduleName) && !isLoading
        ? TALL_HEIGHT_HF
        : SHORT_HEIGHT;
    const hideTOC = nuxStep.moduleName === 'NuxEducationLoader' || steps.length <= 1;

    return (
      <Fragment>
        <Box
          dangerouslySetInlineStyle={{
            __style: {
              background: 'rgba(255, 255, 255, 0.8)',
              top: -104,
              zIndex: 999,
            },
          }}
          height={64}
          position="absolute"
          width="100%"
        />
        <Box
          color="white"
          dangerouslySetInlineStyle={{
            __style: {
              margin: '40px 40px 16px',
              opacity: animateIn ? 0 : 1,
              transition: 'height .1s, opacity .1s ease-in .1s',
              textAlign: 'center' /* center multi-line headers */,
            },
          }}
          height={animateIn ? 0 : height}
          overflow="scroll"
        >
          <Box
            alignItems="center"
            direction="column"
            display="flex"
            height="100%"
            minWidth={MIN_HF_STEP_WIDTH}
            position="relative"
          >
            {!hideTOC && (
              <NuxTableOfContents
                currentNuxStep={nuxStep}
                onPrevStepClick={onPrevStepClick}
                renderType={'dots'}
                steps={steps}
                inHomefeedExp
              />
            )}
            {children}
          </Box>
        </Box>
        <Layer>
          <Box
            bottom
            color="white"
            dangerouslySetInlineStyle={{
              __style: {
                opacity: 0.8,
                top: height + EXTRA_VERTICAL_HEIGHT,
                transition: 'height .1s linear .1s',
              },
            }}
            height={animateIn ? 0 : '100%'}
            position="absolute"
            left
            right
          />
        </Layer>
        {isLoading && (
          <Layer>
            <Box
              bottom
              dangerouslySetInlineStyle={{
                __style: { zIndex: 999 } /* necessary to go over header */,
              }}
              height="100vh"
              left
              position="absolute"
              right
              top
            >
              <LoadingMask />
            </Box>
          </Layer>
        )}
      </Fragment>
    );
  };

  render() {
    const {
      children,
      displayHide,
      onHide,
      onPrevStepClick,
      nuxStep,
      steps,
      isLoading,
      showCounter,
      showTableContents,
    } = this.props;

    const stepNames = steps.map((step) => step.moduleName);
    const currentStepIndex = stepNames.indexOf(nuxStep.moduleName);

    /**
    Using boxes instead of gestalt modal because:
     - need to use layer and z-index to cover background pins and header
     - gestalt modal overlay is darker than current nux overlay
     - nux steps do not follow gestalt modal header format/ui
     - nux modal width increases, requires transition
    **/

    return (
      <Experiment name="web_homefeed_nux" activate={false}>
        {({ anyEnabled }) =>
          anyEnabled ? (
            this.renderOnHomefeed()
          ) : (
            <Experiment
              name="biz_growth_update_business_type"
              isEligible={nuxStep.moduleName === 'BizNuxNameTypeStep'}
            >
              {({ anyEnabled: bizUpdateBusinessTypeEnabled }) => (
                <Layer>
                  <Box
                    alignItems="center"
                    dangerouslySetInlineStyle={{
                      __style: { backgroundColor: 'rgba(0, 0, 0, .5)', zIndex: '999' },
                    }}
                    display="flex"
                    height="100vh"
                    justifyContent="center"
                    overflow="scroll"
                    position="fixed"
                    width="100vw"
                    top
                  >
                    <Box
                      color="white"
                      dangerouslySetInlineStyle={{
                        __style: {
                          transition: !SMALL_STEPS.includes(nuxStep.moduleName) ? 'width 0.5s' : '',
                        },
                      }}
                      direction="column"
                      display="flex"
                      height={
                        bizUpdateBusinessTypeEnabled
                          ? HEIGHT_WITH_BIZ_BUSINESS_TYPE_EXPERIMENT
                          : HEIGHT
                      }
                      position="relative"
                      shape="rounded"
                      width={SMALL_STEPS.includes(nuxStep.moduleName) ? SMALL_WIDTH : LARGE_WIDTH}
                    >
                      {isLoading && <LoadingMask />}
                      {showTableContents && (
                        <Fragment>
                          <NuxTableOfContents
                            currentNuxStep={nuxStep}
                            onPrevStepClick={onPrevStepClick}
                            renderType={'dots'}
                            steps={steps}
                          />
                          <Divider />
                        </Fragment>
                      )}
                      <Box flex="grow" height="100%" width="100%">
                        {children}
                      </Box>
                      <Box position="absolute" marginEnd={8} marginTop={8} right top>
                        <NuxStepTracker
                          nuxStepIndex={currentStepIndex}
                          showCounter={showCounter}
                          numSteps={steps.length}
                        />
                        {displayHide &&
                          onHide && (
                            <IconButton
                              accessibilityLabel={i18n._('cancel', 'close nux modal')}
                              icon="cancel"
                              onClick={onHide}
                              size="sm"
                            />
                          )}
                      </Box>
                    </Box>
                  </Box>
                </Layer>
              )}
            </Experiment>
          )
        }
      </Experiment>
    );
  }
}

export default NuxModal;
